import React, { useState, useEffect, useMemo } from 'react';
import { geoNaturalEarth1, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import isoCountries from 'i18n-iso-countries';
import worldData from 'world-atlas/countries-110m.json';
import { BlockedGeoResponse, BlockedGeoCountry } from '../../types';
import { settingsService } from '../../services/api';

// World geometry is bundled (world-atlas topojson) and projected once at
// module load — no runtime fetches, no tile servers, fully self-contained.
const world: any = feature(worldData as any, (worldData as any).objects.countries);
const projection = geoNaturalEarth1().scale(165).translate([490, 260]);
const pathFor = geoPath(projection);

interface CountryShape {
  id: string; // ISO 3166-1 numeric, as used by world-atlas
  name: string;
  d: string;
}

const shapes: CountryShape[] = world.features
  .map((f: any) => ({ id: String(f.id), name: f.properties?.name || '', d: pathFor(f) || '' }))
  .filter((s: CountryShape) => s.d);

// Log-scaled fill between a pale and deep red — attempt counts span orders
// of magnitude (one scanner can rack up hundreds), so a linear scale would
// flatten everything but the top country.
const heatColor = (attempts: number, maxAttempts: number): string => {
  if (attempts <= 0) return '#e9ecef';
  const t = Math.log1p(attempts) / Math.log1p(Math.max(maxAttempts, 1));
  const from = { r: 255, g: 224, b: 224 };
  const to = { r: 150, g: 12, b: 24 };
  const mix = (a: number, b: number) => Math.round(a + (b - a) * t);
  return `rgb(${mix(from.r, to.r)}, ${mix(from.g, to.g)}, ${mix(from.b, to.b)})`;
};

// The same map serves the SIP/RTP and MCP blocked tabs — only the source of
// the per-country totals differs, so it comes in as a prop rather than being
// hard-wired to one endpoint.
interface BlockedAttemptsMapProps {
  fetchGeo?: () => Promise<BlockedGeoResponse>;
  label?: string;
}

const BlockedAttemptsMap: React.FC<BlockedAttemptsMapProps> = ({
  fetchGeo = settingsService.getBlockedGeo,
  label = 'blocked attempt',
}) => {
  const [geo, setGeo] = useState<BlockedGeoResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hovered, setHovered] = useState<{ name: string; data: BlockedGeoCountry | null } | null>(null);

  useEffect(() => {
    let current = true;
    setGeo(null);
    setError(null);
    fetchGeo()
      .then(data => { if (current) setGeo(data); })
      .catch(err => {
        if (!current) return;
        setError(`Failed to fetch ${label} locations`);
        console.error('Error fetching blocked geo data:', err);
      });
    return () => { current = false; };
  }, [fetchGeo, label]);

  // ISO numeric id -> per-country totals, matching world-atlas feature ids.
  const byNumericId = useMemo(() => {
    const map = new Map<string, BlockedGeoCountry>();
    for (const c of geo?.countries ?? []) {
      if (!c.country_code) continue;
      const numeric = isoCountries.alpha2ToNumeric(c.country_code);
      if (numeric) map.set(String(Number(numeric)), c);
    }
    return map;
  }, [geo]);

  if (error) return <div className="error">{error}</div>;
  if (!geo) return <div className="loading">Loading map…</div>;

  const located = geo.countries.filter(c => c.country_code);
  const unlocated = geo.countries.filter(c => !c.country_code);
  const maxAttempts = Math.max(...located.map(c => c.attempts), 1);
  const hoveredData = hovered?.data;

  return (
    <div className="blocked-map">
      <svg viewBox="0 0 980 500" className="blocked-map-svg" role="img" aria-label={`Heat map of ${label} origins`}>
        {shapes.map(s => {
          const data = byNumericId.get(String(Number(s.id))) || null;
          return (
            <path
              key={s.id}
              d={s.d}
              fill={heatColor(data?.attempts ?? 0, maxAttempts)}
              stroke={hovered?.name === s.name ? '#212529' : '#adb5bd'}
              strokeWidth={hovered?.name === s.name ? 1.2 : 0.4}
              onMouseEnter={() => setHovered({ name: s.name, data })}
              onMouseLeave={() => setHovered(null)}
            >
              <title>
                {data
                  ? `${s.name}: ${data.attempts.toLocaleString()} attempts from ${data.ip_count} IP${data.ip_count === 1 ? '' : 's'}`
                  : s.name}
              </title>
            </path>
          );
        })}
      </svg>

      <div className="blocked-map-info">
        {hovered ? (
          hoveredData
            ? <><strong>{hovered.name}</strong> — {hoveredData.attempts.toLocaleString()} attempts from {hoveredData.ip_count} IP{hoveredData.ip_count === 1 ? '' : 's'}</>
            : <><strong>{hovered.name}</strong> — no blocked attempts</>
        ) : (
          <>{geo.total_attempts.toLocaleString()} blocked attempts from {geo.total_ips} IPs · hover a country for detail</>
        )}
      </div>

      <div className="blocked-map-legend">
        <span>fewer</span>
        <span className="blocked-map-gradient" />
        <span>more (log scale)</span>
      </div>

      <table className="sessions-table blocked-map-ranking">
        <thead>
          <tr><th>Country</th><th>Blocked IPs</th><th>Attempts</th></tr>
        </thead>
        <tbody>
          {located.map(c => (
            <tr key={c.country_code}>
              <td>{c.country}</td>
              <td>{c.ip_count}</td>
              <td>{c.attempts.toLocaleString()}</td>
            </tr>
          ))}
          {unlocated.map((c, i) => (
            <tr key={`unlocated-${i}`}>
              <td>{c.country || 'Unknown / not yet located'}</td>
              <td>{c.ip_count}</td>
              <td>{c.attempts.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BlockedAttemptsMap;
